import { batch, createMemo, createSignal, createUniqueId, type JSX } from 'solid-js';
import { t } from '../i18n';
import { createAccessRequestActions } from '../lib/access-request-actions';
import {
  type AccessRequestView,
  ADMIN_ACCESS_FILTERS,
  type AdminAccessFilter,
  useAdminAccessRequests,
} from '../lib/admin-access-requests';
import { usePagedQuery } from '../lib/paged';
import { AdminAccessRequestsTable } from './AdminAccessRequestsTable';
import { AdminInviteDialog } from './AdminInviteDialog';
import { ConfirmDialog } from './ConfirmDialog';
import { EmptyState } from './EmptyState';
import { LoadMore } from './LoadMore';
import { OperatorRefusalAlert } from './OperatorRefusalAlert';
import { PageHeader } from './PageHeader';
import { Panel } from './Panel';
import { QueryBoundary } from './QueryBoundary';
import { Select } from './Select';
import { Skeleton } from './Skeleton';
import { Toast } from './Toast';

// The access-request inbox container (arc 101 slice 01). A status filter over
// `GET /v1/admin/access-requests`, cursor-paginated; Invite opens the provision
// dialog, Decline asks first (`actions.askDecline`) and writes from the confirm. Mounts only for staff (behind
// <AdminGate/>). The writes and the dialog's state are `createAccessRequestActions`;
// paging is `usePagedQuery`; this owns the filter and composes the two.
//
// After a decision the list is RE-READ from page one (`reload`): the decided
// row leaves the `pending` filter, so refetching a deep cursor would append a
// shifted slice on top of what is shown, and a plain reset would answer from
// the 30s cache the write just invalidated.
//
// ONE error signal, two surfaces: while the dialog is open its refusal renders
// inside it (the form must stay open over a 422); a decline's refusal, which has
// no dialog, rides the toast. The `<Toast/>` is muted while the dialog is open
// so a refusal is never shown twice.

export function AdminAccessRequestsPanel(props: { readonly token: string | null }): JSX.Element {
  const [status, setStatus] = createSignal<AdminAccessFilter>('pending');
  const list = usePagedQuery<AccessRequestView>((cursor) =>
    useAdminAccessRequests(status, cursor, 30, props.token),
  );
  const actions = createAccessRequestActions({
    token: props.token,
    onWritten: () => list.reload(),
  });

  // A filter change and the reset are ONE batched write, so the query key jumps
  // straight to the new filter at `cursor=null` rather than firing a throwaway
  // fetch at the cursor the previous filter had reached. A decline confirm still
  // open goes with it: the dialog sits outside the list, so Confirm would
  // otherwise act on a row no longer on screen (the `AdminGrantsPanel` rule).
  const filter = (next: AdminAccessFilter): void =>
    batch(() => {
      setStatus(next);
      list.reset();
      actions.cancelDecline();
    });

  const dialogOpen = (): boolean => actions.inviting() !== null;
  const statusId = createUniqueId();
  // The LAST application asked about: `declining()` goes null the moment the
  // decline settles, and the confirm stays mounted for its close animation — it
  // read "Decline ?" on the way out.
  const declined = createMemo<AccessRequestView | null>(
    (last) => actions.declining() ?? last,
    null,
  );

  return (
    <>
      <PageHeader title={t('admin.access_title')} desc={t('admin.access_subtitle')} />
      <OperatorRefusalAlert refusal={actions.refusal()} />

      <div class="filter-row">
        <div class="admin-filter__select">
          <label id={`${statusId}-label`} for={statusId} class="sr-only">
            {t('admin.access_status_label')}
          </label>
          <Select
            a11y={{ id: statusId }}
            label={t('admin.access_status_label')}
            value={status()}
            options={ADMIN_ACCESS_FILTERS.map((s) => ({
              value: s,
              label: t(`admin.access_filter_${s}`),
            }))}
            onChange={filter}
          />
        </div>
      </div>

      <Panel title={t('admin.access_queue')}>
        <QueryBoundary
          query={list.view}
          isEmpty={(r) => r.length === 0}
          loading={
            <div class="admin__skeletons">
              <Skeleton height="3rem" />
              <Skeleton height="3rem" />
            </div>
          }
          empty={<EmptyState message={t('admin.access_empty')} />}
        >
          {(r) => (
            <>
              <AdminAccessRequestsTable
                rows={r}
                isBusy={actions.isBusy}
                onInvite={actions.askInvite}
                onDecline={actions.askDecline}
              />
              <LoadMore list={list} label={t('admin.load_more')} />
            </>
          )}
        </QueryBoundary>
      </Panel>

      <ConfirmDialog
        open={actions.declining() !== null}
        title={t('admin.access_decline_title', { name: declined()?.name ?? '' })}
        desc={t('admin.access_decline_desc')}
        cancelLabel={t('admin.confirm_cancel')}
        actionLabel={t('admin.access_decline_confirm')}
        destructive
        onCancel={actions.cancelDecline}
        onConfirm={() => void actions.confirmDecline()}
      />
      <AdminInviteDialog
        request={actions.inviting()}
        busy={actions.inviting() !== null && actions.isBusy(actions.inviting()?.id ?? '')}
        error={dialogOpen() ? actions.error() : null}
        result={actions.invited()}
        onSend={(body) => void actions.invite(body)}
        onClose={actions.closeInvite}
      />
      <Toast
        message={dialogOpen() ? null : actions.error()}
        tone="error"
        onDismiss={actions.clearError}
      />
    </>
  );
}
